import React, { useContext, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { Container, List, ListItem, ListItemText } from '@material-ui/core';
import styled from 'styled-components';
import { PikaIcon } from '../components/PikaIcon';
import logo from '../assets/fagblogg.png';
import SettingsIcon from './SettingsIcon';
import { KeycloakUser, UserContext } from '../shared/user';

const MainLayoutContainer = styled.div`
  padding-bottom: 20px;
  min-height: 100%;
`;

const StyledHeader = styled.header`
  background: #fff;
  box-shadow: 1px 1px 2px #f5f5f5;
  margin-bottom: 60px;
  width: 100%;
`;

const LogoWrapper = styled.div`
  display: inline-block;
  position: absolute;
  top: 12px;
  left: 32px;

  @media (max-width: 1440px) {
    display: none;
  }
`;

const Logo = styled.img`
  clip-path: inset(0 80px 0 0);
  width: 100px;
`;

const StyledNav = styled.nav`
  font-family: Lato;
  width: 100%;
`;

const NavMenu = styled.ul`
  display: flex;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const NavMenuItem = styled.li`
  padding: 0 20px;

  &:first-child {
    padding-left: 0;
  }

  & > a {
    color: #333;
    display: block;
    font-size: 14px;
    font-weight: 700;
    line-height: 3.456;
    padding: 5px 0;
    text-decoration: none;

    &.active {
      color: #007b5f;
      border-bottom: 4px solid #007b5f;
    }
  }
`;

const UserActions = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  align-items: center;
`;

const UserNameWrapper = styled.div`
  padding: 17px 22px;
  white-space: nowrap;
`;

const SettingsButton = styled.button`
  background: #fff;
  border: 0;
  cursor: pointer;
  outline: none;
  padding: 17px 28px;
  border-left: 1px solid #eee;

  &:hover {
    background: #f7f7f7;
    outline: none;
  }
`;

const UserActionsDropdown = styled.div`
  position: absolute;
  top: 64px;
  right: 0;
  background: #fff;
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.29);
  transition:
    visibility 0.5s ease-out,
    opacity 0.5s ease-out;
  visibility: hidden;
  opacity: 0;

  &.open {
    transition:
      visibility 0.5s ease-out,
      opacity 0.5s ease-out;
    visibility: visible;
    opacity: 1;
  }

  &&& .MuiListItem-root {
    padding-left: 20px;
    padding-right: 20px;

    & > span:first-child {
      margin-right: 10px;
    }
  }
`;

interface MenuInfo {
  name: string;
  path: string;
  exact?: boolean;
}

const authorMenu: MenuInfo[] = [
  {
    name: 'Home',
    path: '/',
    exact: true,
  },
  // {
  //   name: 'Article',
  //   path: '/articles',
  // },
  {
    name: 'Templates',
    path: '/attachments',
  },
  // {
  //   name: 'FAQ',
  //   path: '/faqs',
  // },
  {
    name: 'Video',
    path: '/videos',
  },
  // {
  //   name: 'Event',
  //   path: '/events',
  // },
  {
    name: 'Course',
    path: '/courses',
  },
  {
    name: 'Exam',
    path: '/exams',
  },
  {
    name: 'Diploma Template',
    path: '/diploma-templates',
  },
  // {
  //   name: 'News',
  //   path: '/news',
  // },
];

const ownerMenu: MenuInfo[] = [
  {
    name: 'Company',
    path: '/companies',
  },
  {
    name: 'User',
    path: '/users',
  },
  {
    name: 'Exams',
    path: '/exams',
  },
];

const bhtConsultantMenu: MenuInfo[] = [
  {
    name: 'BHT Document',
    path: '/bht-documents',
  },
];

/**
 * Return the navigation menu based on user role.
 */
const getMenu = (userInfo: KeycloakUser): MenuInfo[] => {
  if (userInfo._isAuthor) {
    return authorMenu;
  }

  if (userInfo._isOwner) {
    return ownerMenu;
  }

  if (userInfo._isBHTConsultant) {
    return bhtConsultantMenu;
  }

  return [];
};

const MainLayout: React.FC = ({ children }): JSX.Element => {
  const { keycloak } = useKeycloak();
  const userInfo = useContext(UserContext);
  const [settingsDropdownIsShown, showSettingsDropdown] =
    useState<boolean>(false);

  const signOut = () => {
    if (keycloak) {
      keycloak.logout();
    }
  };

  return (
    <MainLayoutContainer>
      <StyledHeader>
        <LogoWrapper>
          <Logo src={logo} />
        </LogoWrapper>
        <Container>
          <StyledNav>
            <NavMenu>
              {getMenu(userInfo).map(({ name, path, exact }: MenuInfo) => (
                <NavMenuItem key={path}>
                  <NavLink to={path} activeClassName="active" exact={!!exact}>
                    {name}
                  </NavLink>
                </NavMenuItem>
              ))}
            </NavMenu>
          </StyledNav>
        </Container>
        <UserActions>
          <UserNameWrapper>
            {userInfo ? userInfo.given_name : ''}
          </UserNameWrapper>
          <SettingsButton
            onMouseEnter={() => showSettingsDropdown(true)}
            onMouseLeave={() => showSettingsDropdown(false)}
          >
            <SettingsIcon />
          </SettingsButton>
        </UserActions>
        <UserActionsDropdown
          className={settingsDropdownIsShown ? 'open' : ''}
          onMouseEnter={() => showSettingsDropdown(true)}
          onMouseLeave={() => showSettingsDropdown(false)}
        >
          <List>
            {/* <ListItem button onClick={openUserProfile}>
              <PikaIcon icon="ss-user" />
              <ListItemText primary="My Profile" />
            </ListItem> */}
            <ListItem button onClick={signOut}>
              <PikaIcon icon="ss-logout" />
              <ListItemText primary="Signout" />
            </ListItem>
          </List>
        </UserActionsDropdown>
      </StyledHeader>
      {children}
    </MainLayoutContainer>
  );
};

export default MainLayout;
